import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { ConfirmModal } from 'components/dialog/ModalTemplate'
import SelectBox from 'components/form/SelectBox'
import { useDispatch, useSelector } from 'react-redux'
import {
  createKeywords,
  fetchAdGroupKeywords,
  fetchCampaignAdGroups,
  fetchCampaigns,
  reset,
} from 'features/keywordPlanner/addKeywordsSlice'
import LoadingWrapper from 'components/loading/LoadingWrapper'
import { success, fail } from 'features/toast/Toast'
import { isPresent } from 'utils'
import Limitations from 'features/Limitations'
import Icon from 'components/Icon'
import NoneAnchor from 'components/NoneAnchor'

const { AD_GROUP_MAX_KEYWORDS } = Limitations

const AddKeywordsModal = ({
  selectedKeywords,
  afterConfirm,
  handleClose,
  handleDeselect,
}) => {
  const dispatch = useDispatch()
  const [selectedCampaign, setCampaign] = useState(null)
  const [selectedAdGroup, setAdGroup] = useState(null)
  const {
    campaigns,
    campaignAdGroups,
    targetAdGroupKeywords,
    loading,
    disabled,
  } = useSelector(state => state.keywordPlanner.addKeywords)

  useEffect(() => {
    dispatch(fetchCampaigns())
    return () => dispatch(reset())
  }, [dispatch])

  const filteredKeywords = selectedKeywords.filter(
    text => !targetAdGroupKeywords.find(keyword => keyword.text === text)
  )

  const registableCount = AD_GROUP_MAX_KEYWORDS - targetAdGroupKeywords.length

  const handleSelectCampaign = v => {
    setCampaign(v)
    if (v) {
      dispatch(fetchCampaignAdGroups(v.id))
      setAdGroup(null)
    }
  }
  const handleSelectAdGroup = v => {
    setAdGroup(v)
    if (v) {
      dispatch(fetchAdGroupKeywords(v.id))
    }
  }

  const handleConfirm = async ({ preventClose }) => {
    if (registableCount < filteredKeywords.length) {
      preventClose()
      return fail(
        registableCount === 0
          ? `대상 광고그룹에 키워드를 ${AD_GROUP_MAX_KEYWORDS}개 초과하여 등록할 수 없습니다.`
          : `광고그룹에 이미 등록된 키워드로 인해 ${registableCount}개의 키워드만 등록가능합니다.\n` +
              `${registableCount}개 내에서 복사할 키워드를 다시 선택하세요.`
      )
    }
    const { error, payload } = await dispatch(
      createKeywords({
        adGroupId: selectedAdGroup.id,
        texts: filteredKeywords,
        biddingStrategyType: 'INHERIT_FROM_ADGROUP',
      })
    )
    if (error) {
      const { errorCode, errorMessage } = payload
      if (errorCode === 105006) {
        fail(errorMessage)
      } else if (errorCode === 105004) {
        fail(
          '등록 가능한 키워드 개수를 초과하였습니다.\n미사용 키워드를 삭제 후 다시 시도하세요.'
        )
      } else if (errorCode === 105002) {
        fail(
          '입력 불가능한 문자가 입력되어 있습니다.\n(특수문자 일부, 한자 입력 불가능)'
        )
      } else {
        fail(
          `알 수 없는 이유로 키워드를 저장할 수 없습니다. \n다시 시도하세요.`
        )
      }
    } else {
      const { oldKeywordSize, newKeywordSize } = payload
      if (oldKeywordSize > 0) {
        success(
          `이미 등록된 키워드 ${oldKeywordSize}개를 제외하고\n키워드가 ${newKeywordSize}개 저장되었습니다.`
        )
      } else {
        success(`키워드가 ${newKeywordSize}개 저장되었습니다.`)
      }
      afterConfirm()
    }
  }

  return (
    <ConfirmModal
      title="키워드 바로등록"
      onConfirm={handleConfirm}
      handleClose={handleClose}
      confirmDisabled={
        disabled || selectedAdGroup === null || filteredKeywords.length === 0
      }>
      <p className="desc_copy">
        키워드를 바로등록할 캠페인 및 광고그룹을 선택하세요. (캠페인 및
        광고그룹이 최소 1개 이상 사전 등록되어 있어야 키워드 바로등록이
        가능합니다.)
      </p>
      <div className="select_ad">
        <strong className="tit_copy">
          선택한 키워드가 복사될 프리미엄링크 유형의 캠페인/광고그룹
        </strong>
        {campaigns.length === 0 && !loading ? (
          <p className="txt_info">
            <span className="ico_comm" />
            선택 가능한 캠페인이 존재하지 않습니다.
          </p>
        ) : (
          <LoadingWrapper isLoading={loading}>
            <SelectBox
              value={selectedCampaign}
              onValueChange={handleSelectCampaign}
              placeholder="캠페인 선택">
              {(campaigns || []).map(campaign => (
                <SelectBox.Item key={campaign.id} value={campaign}>
                  {campaign.name}
                </SelectBox.Item>
              ))}
            </SelectBox>
            <SelectBox
              value={selectedAdGroup}
              onValueChange={handleSelectAdGroup}
              error={
                selectedCampaign !== null &&
                !loading &&
                campaignAdGroups.length === 0
              }
              disabled={loading || campaignAdGroups.length === 0}
              placeholder={
                selectedCampaign !== null &&
                !loading &&
                campaignAdGroups.length === 0
                  ? '선택 가능한 광고그룹이 존재하지 않습니다.'
                  : '광고그룹 선택'
              }>
              {campaignAdGroups.map(adGroup => (
                <SelectBox.Item key={adGroup.id} value={adGroup}>
                  {adGroup.name}
                </SelectBox.Item>
              ))}
            </SelectBox>
          </LoadingWrapper>
        )}
      </div>
      <div className="select_ad">
        <strong className="tit_copy">
          선택한 키워드&nbsp;
          <span
            className={
              registableCount < filteredKeywords.length ? 'fc_warn' : 'fc_emph'
            }>
            {filteredKeywords.length}
          </span>
          /{registableCount}
        </strong>
        <div className="list_keyword">
          {isPresent(filteredKeywords) ? (
            filteredKeywords.map(keyword => (
              <span key={keyword} className="txt_keyword">
                {keyword}
                <NoneAnchor
                  className="link_del"
                  onClick={() => handleDeselect(keyword)}>
                  <Icon name="del">삭제</Icon>
                </NoneAnchor>
              </span>
            ))
          ) : (
            <span className="fc_info">
              대상 광고그룹에 존재하는 키워드를 제외하고 복사 가능한 키워드가
              없습니다.
            </span>
          )}
        </div>
      </div>
    </ConfirmModal>
  )
}

AddKeywordsModal.propTypes = {
  selectedKeywords: PropTypes.arrayOf(PropTypes.string).isRequired,
  afterConfirm: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleDeselect: PropTypes.func.isRequired,
}

export default AddKeywordsModal
